import * as geom from "../utils/geometry-util.js";
import * as util from "../utils/utils.js";
import navNorthFollowIcon from '../../icons/nav_north.svg?url';
import navCourseUpIcon from '../../icons/nav_northeast.svg?url';
import navNorthFreeIcon from '../../icons/location.svg?url';

const COG_AVG_SIZE = 50;
const TURN_RATE_DEG_PER_MS = 60 / 1000;
const MIN_FRAME_MS = 1000 / 15;
const TERRAIN_EXAGGERATION = 2;

export const VIEW_MODE = {
    NORTH_UP_FOLLOW: "NorthUpFollow",
    COURSE_UP_FOLLOW: "CourseUpFollow",
    NORTH_UP_FREE: "NorthUpFree"
};

const VIEW_MODE_CYCLE_STD = [VIEW_MODE.NORTH_UP_FOLLOW, VIEW_MODE.NORTH_UP_FREE];
const VIEW_MODE_CYCLE_TERRAIN = [VIEW_MODE.NORTH_UP_FOLLOW, VIEW_MODE.COURSE_UP_FOLLOW, VIEW_MODE.NORTH_UP_FREE];

/* TrackMapView — map camera state and view mode */

// Wires UI controls and map interaction events to a TrackMapView instance.
// Returns applyViewMode so callers can trigger mode changes with icon updates.
export function initTrackMapViewListeners(mapView) {

    const VIEW_MODE_ICONS = {
        [VIEW_MODE.NORTH_UP_FOLLOW]: navNorthFollowIcon,
        [VIEW_MODE.COURSE_UP_FOLLOW]: navCourseUpIcon,
        [VIEW_MODE.NORTH_UP_FREE]: navNorthFreeIcon,
    };

    const ctrlTerrain = document.getElementById('ctrlTerrain');
    const ctrlViewMode = document.getElementById('ctrlViewMode');
    const btnRotateLeft = document.getElementById('btnRotateLeft');
    const btnRotateRight = document.getElementById('btnRotateRight');


    const terrainDrawer = document.getElementById('terrainDrawer');
    let _terrainDrawerHideTimer = { id: null };
    util.initialiseTimer(terrainDrawer, terrainDrawer, 800, _terrainDrawerHideTimer);
    util.initialiseTimer(ctrlTerrain, terrainDrawer, 800, _terrainDrawerHideTimer);


    mapView.onModeChange = (mode, auto) => {
        ctrlViewMode.querySelector('img').src = VIEW_MODE_ICONS[mode];
        if (auto) {
            ctrlViewMode.classList.remove('flash');
            void ctrlViewMode.offsetWidth;
            ctrlViewMode.classList.add('flash');
        }
    };

    ctrlTerrain.addEventListener('click', () => {
        mapView.toggleTerrainMode();
        if (mapView.isTerrainMode())
            ctrlTerrain.classList.add('selected');
        else
            ctrlTerrain.classList.remove('selected');
    });

    ctrlViewMode.addEventListener('click', () => {
        mapView.cycleViewMode();
    });

    btnRotateLeft.addEventListener('click', () => mapView.addMapBearingOffset(-45));
    btnRotateRight.addEventListener('click', () => mapView.addMapBearingOffset(45));
}


export class TrackMapView {
    constructor(map) {
        this.map = map;

        this.viewMode = VIEW_MODE.NORTH_UP_FOLLOW;
        this.terrainMode = false;
        this.bearingOffset = 0;
        this.bearingOffsetChanged = false;
        this.autoCenterAllowed = true;
        this.mapHeading = 0;
        this.lastMapHeadingTs = Date.now();
        this.cogAvgArray = [];
        this.cogAvgIdx = 0;
        this.oldViewExtentState = null;
        this._lastFrame = null;
        this.onModeChange = null;
        this.onTerrainModeChange = null;

        map.on('dragstart', () => this.applyViewMode(VIEW_MODE.NORTH_UP_FREE, true));
        map.on('mousedown', () => this.setAutoCenterAllowed(false));
        map.on('mouseup', () => this.setAutoCenterAllowed(true));
        map.on('touchstart', () => this.setAutoCenterAllowed(false));
        map.on('touchend', () => this.setAutoCenterAllowed(true));
        map.on('zoomstart', () => this.setAutoCenterAllowed(false));
        map.on('zoomend', () => this.setAutoCenterAllowed(true));
    }

    isTerrainMode() {
        return this.terrainMode;
    }

    getViewMode() {
        return this.viewMode;
    }

    applyViewMode(mode, auto = false) {
        const changed = mode !== this.viewMode;
        this.viewMode = mode;
        this.initViewMode(auto && changed);
    }

    initViewMode(auto = false) {
        if (this.viewMode === VIEW_MODE.NORTH_UP_FOLLOW || this.viewMode === VIEW_MODE.NORTH_UP_FREE)
            this.setMapBearingOffset(0);
        this.onModeChange?.(this.viewMode, auto);
    }

    setAutoCenterAllowed(flag) {
        this.autoCenterAllowed = flag;
    }

    setMapBearingOffset(offset) {
        this.bearingOffset = geom.normaliseTo180To180(offset);
        this.bearingOffsetChanged = true;
        console.log("Map bearing:" + this.map?.getBearing(), " offset: " + this.bearingOffset);
        this.setMapView(this._lastFrame);
    }

    addMapBearingOffset(offset) {
        this.setMapBearingOffset(this.bearingOffset + offset);
    }

    setMapView(frame) {
        this._lastFrame = frame;

        if (frame == null || frame.lon == null || frame.lat == null || Number.isNaN(frame.lon) || Number.isNaN(frame.lat) || frame._cog == null)
            return;

        if (this.map.isEasing() || !this.autoCenterAllowed)
            return;

        const viewExtentState = `${frame.lon}|${frame.lat}|${this.bearingOffset}|${this.viewMode}|${frame._cog}`;
        if (viewExtentState === this.oldViewExtentState)
            return;
        this.oldViewExtentState = viewExtentState;

        if (this.viewMode === VIEW_MODE.COURSE_UP_FOLLOW) {
            this.cogAvgArray[this.cogAvgIdx] = frame._cog;
            this.cogAvgIdx = (this.cogAvgIdx + 1) % COG_AVG_SIZE;
            const avgCog = util.averageAngle(this.cogAvgArray);

            const now = Date.now();
            if (this.lastMapHeadingTs < now + MIN_FRAME_MS)
                this.lastMapHeadingTs = now - MIN_FRAME_MS;
            const rateAllowed = TURN_RATE_DEG_PER_MS * (now - this.lastMapHeadingTs);
            this.lastMapHeadingTs = now;

            const targetMapBearing = avgCog - this.bearingOffset;
            const diff = geom.normaliseTo180To180(targetMapBearing - this.map.getBearing());
            let rotation = Math.min(rateAllowed, Math.abs(diff)) * Math.sign(diff);
            rotation = util.cubicSmooth(diff, 0, 30) * rotation;
            this.mapHeading = geom.normaliseTo180To180(this.map.getBearing() + rotation);
            this.map.setCenter([frame.lon, frame.lat]);
            this.map.setBearing(this.mapHeading);
            this.bearingOffsetChanged = false;
            return;
        }

        if (!geom.bearingsEqual(this.map.getBearing(), this.bearingOffset)) {
            console.log(this.map.getBearing(), ",", this.bearingOffset);
            this.map.easeTo({ bearing: geom.normaliseTo180To180(this.bearingOffset), duration: 300 });
            return;
        }

        if (this.viewMode !== VIEW_MODE.NORTH_UP_FREE)
            this.map.setCenter([frame.lon, frame.lat]);
    }

    toggleTerrainMode() {
        this.setAutoCenterAllowed(false);
        this.setMapBearingOffset(0);
        if (this.terrainMode) {
            this.map.setTerrain(null);
            this.map.easeTo({ pitch: 0, bearing: 0, duration: 500 }).once('moveend', () => {
                this.setAutoCenterAllowed(true);
                this.applyViewMode(VIEW_MODE.NORTH_UP_FOLLOW);
            });
        } else {
            this.map.setTerrain({ source: 'terrarium', exaggeration: TERRAIN_EXAGGERATION });
            this.map.easeTo({ pitch: 60, bearing: 0, duration: 500 }).once('moveend', () => {
                this.setAutoCenterAllowed(true);
                this.applyViewMode(VIEW_MODE.COURSE_UP_FOLLOW);
            });
        }
        this.terrainMode = !this.terrainMode;
        this.onTerrainModeChange?.(this.terrainMode);
        return this.terrainMode;
    }

    cycleViewMode() {
        const cycle = this.terrainMode ? VIEW_MODE_CYCLE_TERRAIN : VIEW_MODE_CYCLE_STD;
        const next = cycle[(cycle.indexOf(this.viewMode) + 1) % cycle.length];
        this.applyViewMode(next);
    }
}
